import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Undo2, Search, Loader2, AlertTriangle, Users2, FileCheck, Pencil, WifiOff, CloudUpload, Printer, HelpCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PREDICTION_LABELS, PREDICTION_OPTIONS } from "@/lib/constants";
import { enqueueMark, readQueue, flushQueue } from "@/lib/offlineQueue";

const PRED_STYLE = {
  KESIN: { bg: "#dcfce7", fg: "#15803d", label: "KESİN" },
  BELKI: { bg: "#fef3c7", fg: "#b45309", label: "BELKİ" },
  KARSI: { bg: "#fee2e2", fg: "#b91c1c", label: "KARŞI" },
  GELMEYECEK: { bg: "#e2e8f0", fg: "#475569", label: "GELMEYECEK" },
  ULASILAMADI: { bg: "#e0f2fe", fg: "#0369a1", label: "ULAŞILAMADI" },
  "": { bg: "#f1f5f9", fg: "#64748b", label: "EĞİLİM YOK" },
};

const fold = (s) => (s || "").toLocaleLowerCase("tr").replace(/[ıİ]/g, "i").replace(/[şŞ]/g, "s")
  .replace(/[ğĞ]/g, "g").replace(/[üÜ]/g, "u").replace(/[öÖ]/g, "o").replace(/[çÇ]/g, "c");

const hhmm = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const TALLY_OPTS = [
  { key: "KESIN", label: "KESİN", desc: "Bizim oyumuz", bg: "#16A34A", ring: "#15803D" },
  { key: "BELIRSIZ", label: "BELİRSİZ", desc: "Kime verdiği bilinmiyor", bg: "#64748B", ring: "#475569" },
  { key: "KARSI", label: "KARŞI", desc: "Rakibe gitti", bg: "#DC2626", ring: "#B91C1C" },
];
const TALLY_MAP = Object.fromEntries(TALLY_OPTS.map((o) => [o.key, o]));

function TallyBadge({ c }) {
  const o = TALLY_MAP[c];
  if (!o) return null;
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white"
      style={{ background: o.bg }}>{o.label}</span>
  );
}

function PredBadge({ p }) {
  const s = PRED_STYLE[p] || PRED_STYLE[""];
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide"
      style={{ background: s.bg, color: s.fg }}>{s.label}</span>
  );
}

function VoterBox({ row, size, onTap, onEdit, canWrite }) {
  const disabled = row.status !== "faal";
  const pad = size === "lg" ? "p-4" : size === "sm" ? "p-2" : "p-3";
  const nameSize = size === "lg" ? "text-lg" : size === "sm" ? "text-xs" : "text-sm";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !disabled && canWrite && onTap(row)}
      data-testid={`ballot-box-${row.id}`}
      className={`relative rounded-xl border text-left transition-all ${pad} ${
        disabled ? "opacity-55 cursor-not-allowed bg-muted/40 border-border"
          : row.marked
            ? "cursor-pointer border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
            : "cursor-pointer border-border bg-card hover:border-[#0F2C59] hover:shadow-md active:scale-[0.99]"
      }`}
    >
      {row.marked && (
        <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-emerald-600 text-white"
          data-testid={`ballot-check-${row.id}`}>
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
      )}
      <p className={`font-semibold pr-7 leading-snug ${nameSize} ${row.marked ? "line-through text-muted-foreground" : ""}`}>
        {row.company_name}
      </p>
      <p className={`text-xs mt-1 truncate ${row.people_from_document ? "font-semibold text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}
        title={row.people_from_document ? "İsim yetki belgesinden alındı" : undefined}>
        {row.people?.length ? row.people[0] : "— yetkili girilmemiş —"}
        {row.people?.length > 1 && <span className="ml-1 font-semibold">+{row.people.length - 1}</span>}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {row.marked && row.tally_prediction
          ? <TallyBadge c={row.tally_prediction} />
          : <PredBadge p={row.prediction} />}
        {row.sicil_no && <span className="text-[10px] text-muted-foreground">#{row.sicil_no}</span>}
        {row.entity_class === "sahis" ? (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
            <Users2 className="h-3 w-3" /> ŞAHIS
          </span>
        ) : row.proxy_document && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-sky-700 dark:text-sky-300">
            <FileCheck className="h-3 w-3" /> belge
          </span>
        )}
        {disabled && <Badge variant="outline" className="text-[10px] py-0">ASKI</Badge>}
        {row.marked && <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">{hhmm(row.marked_at)}</span>}
      </div>
      {canWrite && !disabled && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(row); }}
          data-testid={`ballot-edit-pred-${row.id}`}
          className="absolute bottom-2 right-2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function CommitteeBallot({ committeeNo, onBack }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("bekleyen");
  const [q, setQ] = useState("");
  const [size, setSize] = useState("md");
  const [busyId, setBusyId] = useState(null);
  const [lastMark, setLastMark] = useState(null);
  const [edit, setEdit] = useState(null);
  const [pred, setPred] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [queued, setQueued] = useState(readQueue().length);
  const [online, setOnline] = useState(navigator.onLine);
  const [flushing, setFlushing] = useState(false);
  const [justMarked, setJustMarked] = useState([]);
  const [tally, setTally] = useState(null);
  const [tallyBusy, setTallyBusy] = useState(null);

  const openTally = (row) => setTally(row);

  const saveTally = async (row, choice) => {
    setTallyBusy(choice);
    const wasMarked = row.marked;
    try {
      const { data: res } = await api.post("/election-day/tally", { voter_id: row.id, choice });
      setData((d) => ({
        ...d,
        rows: d.rows.map((x) => (x.id === row.id
          ? { ...x, marked: true, tally_prediction: res.tally_prediction,
              marked_at: res.marked_at, marked_by: res.marked_by }
          : x)),
        marked_count: d.marked_count + (wasMarked ? 0 : 1),
      }));
      if (!wasMarked) {
        setLastMark({ ...row, marked: true, marked_at: res.marked_at });
        setJustMarked((s) => [...s, row.id]);
        setTimeout(() => setJustMarked((s) => s.filter((i) => i !== row.id)), 2500);
      }
      toast.success(`${row.company_name} — ${TALLY_MAP[choice].label}`);
      setTally(null);
    } catch (e) {
      const st = e.response?.status;
      if (!st || st >= 500) {
        const n = enqueueMark({ voter_id: row.id, target: true, name: row.company_name, choice });
        setQueued(n);
        toast.warning(`Bağlantı yok — ${row.company_name} kuyruğa alındı`);
        setTally(null);
      } else {
        toast.error(formatApiError(e.response?.data?.detail, e));
      }
    } finally {
      setTallyBusy(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/election-day/committee/${committeeNo}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  }, [committeeNo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const canWrite = !!data?.state?.can_tally;

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (tab === "bekleyen") r = r.filter((x) => !x.marked || justMarked.includes(x.id));
    if (tab === "gelen") r = r.filter((x) => x.marked);
    if (q.trim()) {
      const t = fold(q.trim());
      r = r.filter((x) => fold(x.company_name).includes(t) || (x.sicil_no || "").includes(q.trim())
        || (x.people || []).some((p) => fold(p).includes(t)));
    }
    return r;
  }, [data, tab, q, justMarked]);

  const powerGroups = useMemo(() => {
    const map = new Map();
    (data?.rows || []).forEach((r) => {
      const name = (r.voting_person || "").trim();
      if (!name || r.status !== "faal") return;
      const k = fold(name);
      if (!map.has(k)) map.set(k, { person: name, rows: [] });
      map.get(k).rows.push(r);
    });
    let gs = [...map.values()].filter((g) => g.rows.length >= 2).map((g) => ({
      ...g,
      votes: g.rows.length,
      voted: g.rows.filter((r) => r.marked).length,
    }));
    if (q.trim()) {
      const t = fold(q.trim());
      gs = gs.filter((g) => fold(g.person).includes(t)
        || g.rows.some((r) => fold(r.company_name).includes(t) || (r.sicil_no || "").includes(q.trim())));
    }
    gs.sort((a, b) => b.votes - a.votes || a.person.localeCompare(b.person, "tr"));
    return gs;
  }, [data, q]);

  const downloadVotersPdf = async () => {
    try {
      const res = await api.get(`/election-day/committee/${committeeNo}/voters-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `K${committeeNo}_secmen_ciktisi.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const toggle = async (row) => {
    const target = !row.marked;
    // Önce ekranı güncelle (tek dokunuş hissi), sonra sunucuya gönder.
    setData((d) => ({
      ...d,
      rows: d.rows.map((x) => (x.id === row.id
        ? { ...x, marked: target, marked_at: target ? new Date().toISOString() : null }
        : x)),
      marked_count: d.marked_count + (target ? 1 : -1),
    }));
    setLastMark(target ? { ...row, marked: true, marked_at: new Date().toISOString() } : null);
    if (target) {
      setJustMarked((s) => [...s, row.id]);
      setTimeout(() => setJustMarked((s) => s.filter((i) => i !== row.id)), 2500);
    } else {
      setJustMarked((s) => s.filter((i) => i !== row.id));
    }
    setBusyId(row.id);
    try {
      const { data: res } = await api.post("/election-day/mark", { voter_id: row.id });
      if (res.marked !== target) {
        setData((d) => ({
          ...d,
          rows: d.rows.map((x) => (x.id === row.id ? { ...x, marked: res.marked } : x)),
          marked_count: d.marked_count + (res.marked ? 1 : -1) - (target ? 1 : -1),
        }));
      }
      toast.success(res.marked ? `${row.company_name} — oy kullandı` : `${row.company_name} — geri alındı`);
    } catch (e) {
      const st = e.response?.status;
      if (!st || st >= 500) {
        const n = enqueueMark({ voter_id: row.id, target, name: row.company_name });
        setQueued(n);
        toast.warning(`Bağlantı yok — ${row.company_name} kuyruğa alındı`);
      } else {
        // Kalıcı hata: ekranı geri al
        setData((d) => ({
          ...d,
          rows: d.rows.map((x) => (x.id === row.id ? { ...x, marked: !target } : x)),
          marked_count: d.marked_count - (target ? 1 : -1),
        }));
        setLastMark(null);
        toast.error(formatApiError(e.response?.data?.detail, e));
      }
    } finally {
      setBusyId(null);
    }
  };

  const flush = useCallback(async (silent) => {
    if (!readQueue().length) return;
    setFlushing(true);
    try {
      const r = await flushQueue();
      setQueued(r.left);
      if (r.sent && !silent) toast.success(`${r.sent} işaretleme gönderildi`);
      if (r.failed) toast.error(`${r.failed} işaretleme gönderilemedi (yetki/ASKI)`);
      if (r.sent || r.failed) load();
    } finally {
      setFlushing(false);
    }
  }, [load]);

  useEffect(() => {
    const on = () => { setOnline(true); flush(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const id = setInterval(() => { if (navigator.onLine) flush(true); }, 15000);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      clearInterval(id);
    };
  }, [flush]);

  const openEdit = (row) => { setEdit(row); setPred(row.prediction || ""); setReason(""); };

  const savePred = async () => {
    setSaving(true);
    try {
      await api.put("/election-day/prediction", {
        voter_id: edit.id, prediction: pred,
        loss_reason: reason || undefined,
      });
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === edit.id ? { ...x, prediction: pred } : x)) }));
      toast.success("Eğilim güncellendi");
      setEdit(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Sandık yükleniyor...</div>;
  }

  const pct = data.faal_count ? Math.round(data.marked_count / data.faal_count * 100) : 0;
  const needReason = (edit?.prediction === "KESIN") && pred === "KARSI";

  return (
    <div className="space-y-4" data-testid="committee-ballot">
      <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b border-border sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="ballot-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Komiteler
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base md:text-lg font-bold truncate">
              {data.committee_no}. {data.name}
            </h2>
            <p className="text-xs text-muted-foreground">
              Oy kullanan <b className="text-foreground" data-testid="ballot-progress">{data.marked_count}/{data.faal_count}</b> · %{pct}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/cetele-kilavuzu")} data-testid="ballot-guide-shortcut">
            <HelpCircle className="h-4 w-4 mr-1" /> Nasıl yapılır?
          </Button>
          <Button variant="outline" size="sm" onClick={downloadVotersPdf} data-testid="ballot-voters-pdf">
            <Printer className="h-4 w-4 mr-1" /> Seçmen Çıktısı (PDF)
          </Button>
          <div className="flex items-center gap-1 rounded-lg border border-border p-0.5" data-testid="ballot-size">            {["sm", "md", "lg"].map((s) => (
              <button key={s} type="button" onClick={() => setSize(s)}
                data-testid={`ballot-size-${s}`}
                className={`px-2 py-1 text-xs font-semibold rounded ${size === s ? "bg-[#0F2C59] text-white" : "text-muted-foreground"}`}>
                {s === "sm" ? "A−" : s === "md" ? "A" : "A+"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {[["bekleyen", "Bekleyen"], ["gelen", "Oy kullanan"], ["tumu", "Tümü"], ["guc", "Oy Gücü"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              data-testid={`ballot-tab-${k}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                tab === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {l}
              <span className="ml-1.5 opacity-70">
                {k === "bekleyen" ? data.faal_count - data.marked_count : k === "gelen" ? data.marked_count
                  : k === "guc" ? powerGroups.length : data.rows.length}
              </span>
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, yetkili veya sicil ara"
              className="pl-8" data-testid="ballot-search" />
          </div>
        </div>
      </div>

      {!canWrite && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="ballot-readonly">
          <AlertTriangle className="h-4 w-4" /> Bu ekranı yalnız görüntüleyebilirsiniz.
        </div>
      )}

      {(!online || queued > 0) && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-950/30 dark:text-sky-200" data-testid="ballot-offline-bar">
          {online ? <CloudUpload className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
          {queued > 0
            ? <span><b>{queued}</b> işaretleme gönderilmeyi bekliyor{online ? " — gönderiliyor..." : " (bağlantı yok)"}</span>
            : <span>Bağlantı yok — işaretlemeler kuyruğa alınacak, bağlantı gelince otomatik gönderilecek.</span>}
          {queued > 0 && online && (
            <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={() => flush()}
              disabled={flushing} data-testid="ballot-flush-queue">
              {flushing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Şimdi gönder"}
            </Button>
          )}
        </div>
      )}


      <p className="text-sm text-muted-foreground">
        Oy kullanan üyenin kutusuna dokunun: KESİN / BELİRSİZ / KARŞI seçenekleri açılır, bir dokunuşla
        kaydedilir. Mevcut eğilim seçili gelir. Çetele seçimleri firmanın oy eğilimi kaydını değiştirmez.
      </p>

      {lastMark && canWrite && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 dark:bg-emerald-950/30" data-testid="ballot-last-mark">
          <span className="text-sm truncate">
            Son işaretlenen: <b>{lastMark.company_name}</b> · {hhmm(lastMark.marked_at)}
          </span>
          <Button size="sm" variant="outline" onClick={() => toggle(lastMark)} disabled={busyId === lastMark.id}
            data-testid="ballot-undo-last">
            <Undo2 className="h-4 w-4 mr-1" /> Geri al
          </Button>
        </div>
      )}

      {tab === "guc" ? (
        powerGroups.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground" data-testid="ballot-power-empty">
            <Users2 className="h-6 w-6 mx-auto mb-2 opacity-60" />
            2 ve daha fazla oyu olan yetkili bulunamadı.
          </Card>
        ) : (
          <div className="space-y-3" data-testid="ballot-power-list">
            <p className="text-sm text-muted-foreground">
              Aynı yetkili adına 2 ve daha fazla oy bağlı kişiler, oy gücü çoktan aza sıralanır.
              Oy kullanılan firmanın üzeri çizilir.
            </p>
            {powerGroups.map((g, i) => {
              const done = g.voted === g.votes;
              return (
                <Card key={g.person + i} className={`p-3.5 border ${done ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border"}`}
                  data-testid={`ballot-power-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="grid h-8 min-w-8 place-items-center rounded-lg bg-[#0F2C59] px-2 text-sm font-bold text-white tabular-nums">
                      {g.votes}
                    </span>
                    <p className={`font-semibold text-sm md:text-base ${done ? "line-through text-muted-foreground" : ""}`}>
                      {g.person}
                    </p>
                    <span className="ml-auto text-xs font-semibold text-muted-foreground tabular-nums">
                      {g.voted}/{g.votes} oy kullandı
                    </span>
                  </div>
                  <div className="mt-2.5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {g.rows.map((r) => (
                      <button key={r.id} type="button" disabled={!canWrite}
                        onClick={() => canWrite && openTally(r)}
                        data-testid={`ballot-power-firm-${r.id}`}
                        className={`rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${
                          r.marked ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                            : "border-border bg-card hover:border-[#0F2C59]"}`}>
                        <span className={`block font-semibold ${r.marked ? "line-through text-muted-foreground" : ""}`}>
                          {r.company_name}
                        </span>
                        <span className="mt-1 flex items-center gap-1.5">
                          {r.marked && r.tally_prediction ? <TallyBadge c={r.tally_prediction} /> : <PredBadge p={r.prediction} />}
                          {r.marked && <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">{hhmm(r.marked_at)}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="ballot-empty">
          <Users2 className="h-6 w-6 mx-auto mb-2 opacity-60" />
          Bu sekmede kayıt yok.
        </Card>
      ) : (
        <div className={`grid gap-2.5 ${
          size === "lg" ? "sm:grid-cols-2 lg:grid-cols-3" : size === "sm"
            ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        }`} data-testid="ballot-grid">
          {rows.map((r) => (
            <VoterBox key={r.id} row={r} size={size} canWrite={canWrite}
              onTap={busyId ? () => {} : openTally} onEdit={openEdit} />
          ))}
        </div>
      )}


      <Dialog open={!!tally} onOpenChange={(o) => { if (!o) setTally(null); }}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="ballot-tally-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base leading-snug">{tally?.company_name}</DialogTitle>
          </DialogHeader>
          {tally && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="max-h-16 overflow-y-auto pr-1" data-testid="tally-people-box">
                  <span className={tally.people_from_document ? "font-semibold text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>
                    {tally.people?.length ? tally.people.join(" · ") : "— yetkili girilmemiş —"}
                  </span>
                </div>
                {tally.people_from_document && <span className="block text-[11px] text-emerald-600">isim yetki belgesinden</span>}
              </div>
              <p className="text-xs text-muted-foreground">
                Oy hangi tarafa gitti? Seçim yalnız çeteleyi etkiler, firmanın oy eğilimi kaydı değişmez.
              </p>
              <div className="grid gap-2">
                {TALLY_OPTS.map((o) => {
                  const current = tally.tally_prediction
                    || (["KESIN", "KARSI"].includes(tally.prediction) ? tally.prediction : "");
                  const active = current === o.key;
                  return (
                    <button key={o.key} type="button" disabled={!!tallyBusy}
                      onClick={() => saveTally(tally, o.key)}
                      data-testid={`tally-opt-${o.key}`}
                      className={`flex items-center justify-between rounded-xl px-4 py-4 text-left text-white transition-all active:scale-[0.99] ${active ? "ring-4 ring-offset-2 ring-offset-background" : "opacity-90 hover:opacity-100"}`}
                      style={{ background: o.bg, ...(active ? { "--tw-ring-color": o.ring } : {}) }}>
                      <span>
                        <span className="block text-lg font-bold tracking-wide">{o.label}</span>
                        <span className="block text-xs opacity-90">{o.desc}</span>
                      </span>
                      {tallyBusy === o.key
                        ? <Loader2 className="h-5 w-5 animate-spin" />
                        : active && <Check className="h-6 w-6" strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
              {tally.marked && (
                <Button variant="outline" className="w-full" disabled={!!tallyBusy}
                  onClick={() => { toggle(tally); setTally(null); }} data-testid="tally-undo">
                  <Undo2 className="h-4 w-4 mr-1.5" /> Oyu geri al (oy kullanmadı)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="ballot-pred-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Eğilimi değiştir</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{edit?.company_name}</p>
          <div className="space-y-3">
            <Select value={pred} onValueChange={setPred}>
              <SelectTrigger data-testid="ballot-pred-select"><SelectValue placeholder="Eğilim seç" /></SelectTrigger>
              <SelectContent>
                {PREDICTION_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p}>{PREDICTION_LABELS[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {needReason && (
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="ballot-loss-reason"><SelectValue placeholder="Kayıp nedeni (zorunlu)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rakip_aday">Rakip aday ilişkisi</SelectItem>
                  <SelectItem value="ortaklik">Ortaklık/akrabalık</SelectItem>
                  <SelectItem value="memnuniyetsizlik">Memnuniyetsizlik</SelectItem>
                  <SelectItem value="kararsiz">Ulaşılamıyor/kararsız</SelectItem>
                  <SelectItem value="diger">Diğer</SelectItem>
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              Değişiklik ana kayda da yazılır ve işlem günlüğüne düşer. Firma işaretliyse oyu yeni
              eğilime göre sayılır.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Vazgeç</Button>
            <Button onClick={savePred} disabled={saving || !pred || (needReason && !reason)} data-testid="ballot-pred-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
